// CmFormCaptureFields — renders a form-bound CmTable row's capture inputs inline
// inside EntityForm, in the author's own order.
//
// When a CM table is bound to a CmForm, that form IS the record editor: its fields
// render directly in the record column, including the ones bound to the row's own
// `name` / `description` columns (EntityForm drops its generic inputs for those —
// see `recordTitle.ts`). Values flow as one flat capture map (`captureKeys.ts`),
// so conditional logic, calculations and validation treat every field the same
// whichever column it lands in.
//
// Not rendered here: media (photo/video/audio/attachment/signature/sketch), which
// EntityForm captures through its own media flow, geometry (the map), and record
// links — all of which stay where they were.
//
// The active form's `uuid` is reported up via `onActiveFormChange` so EntityForm
// sends it as `form_uuid` on save, re-stamping the row's provenance tag.

import { useEffect, useMemo, useRef } from "react";
import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FieldForm, SchemaField } from "../../../types/fieldForm";
import { isFieldRequired, isFieldVisible } from "../../../utilities/formBuilder/conditions";
import { computeCalculatedValues } from "../../../utilities/formBuilder/calculations";
import { captureKeyOf, CaptureValues } from "../../../utilities/formBuilder/captureKeys";
import { collectCaptureErrors, isCapturable, isValueEmpty } from "../../../utilities/formBuilder/captureValidation";
import { buildSchemaLayout, LayoutNode } from "../../../utilities/formBuilder/sections";
import { isMediaWidget } from "../../form-builder/fieldTypes";
import { ResolvedRowForm } from "./useResolvedRowForm";
import { SchemaFieldInput } from "./SchemaFieldInput";

interface Props {
  /** The form resolved for this row (see `useResolvedRowForm`). */
  resolved: ResolvedRowForm;
  /** Flat capture map — `row_data` plus the reserved `$name` / `$description` slots. */
  values: CaptureValues;
  /** Called with the next capture map (EntityForm splits it back onto the row). */
  onChange: (next: CaptureValues) => void;
  disabled?: boolean;
  /** Whether the host is creating a new row or editing an existing one. */
  mode?: "create" | "edit";
  /**
   * Reports capture validation errors (keyed by capture key) for the visible
   * fields, so the host can block a save while any required/invalid field remains.
   */
  onValidityChange?: (errors: Record<string, string>) => void;
  /** Reveal inline errors — the host sets this after a blocked save attempt. */
  showErrors?: boolean;
  /**
   * The config says this table is bound to a form, so a brief "loading" line is
   * worth showing. A table with no form stays silent while the lookup settles
   * rather than flashing a message about fields it doesn't have.
   */
  expectsForm?: boolean;
}

/** Fields this component captures: skip display + media, keep every bound column. */
function capturableFields(form: FieldForm | null): SchemaField[] {
  return (form?.schema?.fields ?? []).filter(isCapturable);
}

const CmFormCaptureFields: React.FC<Props> = ({
  resolved,
  values,
  onChange,
  disabled,
  mode = "edit",
  onValidityChange,
  showErrors,
  expectsForm
}) => {
  const { activeForm, published, isLoading, needsPicker, hasTag, showVersionToggle, selection, setSelection, setManualForm } = resolved;

  // Capturable fields (skip display + media; `name`/`description` targets included).
  const fields = useMemo<SchemaField[]>(() => capturableFields(activeForm), [activeForm]);

  // All schema fields — used to resolve condition `field_key` references (a
  // condition may point at any field, including ones not rendered here).
  const allFields = useMemo<SchemaField[]>(() => activeForm?.schema?.fields ?? [], [activeForm]);

  // Nested section layout (sections/labels + their member fields) in author order.
  const layout = useMemo<LayoutNode[]>(() => buildSchemaLayout(allFields), [allFields]);

  // Capture keys of fields that sit inside a currently-hidden section. These are
  // neither rendered nor validated, and their captured values are cleared like a
  // directly-hidden field.
  const sectionHiddenKeys = useMemo(() => {
    const hidden = new Set<string>();
    const walk = (nodes: LayoutNode[], parentHidden: boolean) => {
      for (const node of nodes) {
        if (node.type === "section") {
          const secHidden = parentHidden || !isFieldVisible(node.field, allFields, values);
          walk(node.children, secHidden);
        } else if (parentHidden) {
          const key = captureKeyOf(node.field.target);
          if (key) hidden.add(key);
        }
      }
    };
    walk(layout, false);
    return hidden;
  }, [layout, allFields, values]);

  // When a field becomes hidden, clear its captured value (contract default
  // `visible_conditions_behavior: "clear"`); and keep every visible `calculated`
  // field's stored value in sync with its evaluated expression, so a record
  // created/edited from the web persists the computed result to the CmTable.
  useEffect(() => {
    const patch: Record<string, unknown> = {};
    const computed = computeCalculatedValues(allFields, values);
    for (const f of fields) {
      const key = captureKeyOf(f.target);
      if (!key) continue;
      if (!isFieldVisible(f, allFields, values) || sectionHiddenKeys.has(key)) {
        if (f.visible_conditions_behavior === "keep") continue;
        const v = values[key];
        const empty = v == null || v === "" || (Array.isArray(v) && v.length === 0);
        if (!empty) patch[key] = Array.isArray(v) ? [] : "";
        continue;
      }
      if (f.widget === "calculated" && key in computed) {
        const next = computed[key] ?? "";
        if (String(values[key] ?? "") !== String(next)) patch[key] = next;
      }
    }
    if (Object.keys(patch).length) onChange({ ...values, ...patch });
  }, [fields, allFields, values, onChange, sectionHiddenKeys]);

  // Seed authored defaults into a NEW record once its form resolves: `default_value`,
  // `default_today` (date/datetime), and `default_now` (time). Only fills fields that
  // are still empty, so it never clobbers user input or an existing row's values.
  const seededFormRef = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== "create" || !activeForm) return;
    if (seededFormRef.current === activeForm.uuid) return;
    seededFormRef.current = activeForm.uuid;
    const patch: Record<string, unknown> = {};
    for (const f of fields) {
      const key = captureKeyOf(f.target);
      if (!key || !isValueEmpty(values[key])) continue;
      if (f.default_value != null && f.default_value !== "") patch[key] = f.default_value;
      else if ((f.widget === "date" || f.widget === "datetime") && f.default_today) {
        const now = new Date();
        const date = now.toISOString().slice(0, 10);
        patch[key] = f.widget === "datetime" ? `${date}T${now.toTimeString().slice(0, 5)}` : date;
      } else if (f.widget === "time" && f.default_now) {
        patch[key] = new Date().toTimeString().slice(0, 5);
      }
    }
    if (Object.keys(patch).length) onChange({ ...values, ...patch });
  }, [mode, activeForm, fields, values, onChange]);

  // Report validation errors (required-but-empty / invalid) up so the host can block a save.
  const captureErrors = useMemo(
    () => collectCaptureErrors(fields, allFields, values, sectionHiddenKeys),
    [fields, allFields, values, sectionHiddenKeys]
  );
  useEffect(() => {
    onValidityChange?.(captureErrors);
  }, [captureErrors, onValidityChange]);

  if (isLoading) {
    if (!expectsForm) return null;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant='body2' color='text.secondary'>
          Loading form fields…
        </Typography>
      </Box>
    );
  }

  // Nothing to capture (no forms, or the resolved form has only media/display fields).
  if (!needsPicker && (!activeForm || fields.length === 0)) return null;

  const setValue = (key: string, value: unknown) => onChange({ ...values, [key]: value });

  // Recursively render the section layout: section headers (skipped when hidden or
  // their visibility conditions fail), inline Label text, and the capture inputs.
  const renderLayoutNode = (node: LayoutNode): React.ReactNode => {
    const f = node.field;
    if (node.type === "section") {
      if (!isFieldVisible(f, allFields, values)) return null;
      return (
        <Box key={f.key}>
          <Typography variant='subtitle2' color='grey.800' fontWeight={700} sx={{ mb: "0.5rem" }}>
            {f.label || "Section"}
          </Typography>
          <Stack spacing={"1.25rem"} sx={{ pl: "0.75rem", borderLeft: "2px solid", borderColor: "grey.200" }}>
            {node.children.map((child) => renderLayoutNode(child))}
          </Stack>
        </Box>
      );
    }

    // Display-only Label — informational text shown inline.
    if (f.kind === "display") {
      if (f.widget !== "label") return null;
      if (!isFieldVisible(f, allFields, values)) return null;
      return (
        <Typography key={f.key} variant='body2' color='grey.700' sx={{ whiteSpace: "pre-wrap" }}>
          {f.label}
        </Typography>
      );
    }

    // Media is captured by EntityForm's media flow, not here.
    if (isMediaWidget(f.widget)) return null;

    const key = captureKeyOf(f.target);
    if (key == null) return null;
    if (!isFieldVisible(f, allFields, values)) return null;

    const required = isFieldRequired(f, allFields, values);
    const effective = f.required === required ? f : { ...f, required };
    const emptyError = showErrors && isValueEmpty(values[key]) ? captureErrors[key] : undefined;
    return (
      <Box key={f.key} data-capture-key={key} data-capture-error={emptyError ? "true" : undefined}>
        <SchemaFieldInput field={effective} value={values[key]} onChange={(v) => setValue(key, v)} />
        {emptyError && (
          <Typography variant='caption' color='error.main' sx={{ mt: "0.25rem", display: "block" }}>
            {emptyError}
          </Typography>
        )}
      </Box>
    );
  };

  // Fallback — pick a form for an untagged row (legacy) or a new multi-form row.
  if (needsPicker) {
    return (
      <Stack spacing={"1rem"}>
        {mode === "edit" && !hasTag && (
          <Stack
            direction='row'
            spacing={"0.5rem"}
            alignItems='flex-start'
            sx={{ p: "0.75rem", borderRadius: "0.5rem", bgcolor: "warning.light", color: "warning.contrastText" }}
          >
            <InfoOutlinedIcon fontSize='small' sx={{ mt: "0.125rem" }} />
            <Typography variant='body2'>
              Couldn&apos;t determine which form this record was captured with. Choose one below to view and edit its fields.
            </Typography>
          </Stack>
        )}
        <Typography variant='body2' color='grey.600'>
          {mode === "create" ? "Select a form for this record:" : "Available forms for this table:"}
        </Typography>
        <Stack spacing={"0.5rem"}>
          {published.map((f) => (
            <Box
              key={f.uuid}
              role='button'
              tabIndex={0}
              onClick={() => !disabled && setManualForm(f)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !disabled && setManualForm(f)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: "0.5rem",
                p: "0.75rem 0.875rem",
                cursor: disabled ? "default" : "pointer",
                bgcolor: "#fff",
                "&:hover": disabled ? {} : { borderColor: "primary.main", bgcolor: "grey.50" }
              }}
            >
              <Typography variant='body1' color='grey.800' fontWeight={500} noWrap>
                {f.name || "Untitled form"}
              </Typography>
              <Typography variant='caption' color='grey.500'>
                v{f.version}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={"1.25rem"} sx={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      {/* Version toggle — only when the row's captured version differs from the latest. */}
      {showVersionToggle && (
        <Stack direction='row' spacing={"0.5rem"} alignItems='center'>
          <Chip
            label='Original form'
            size='small'
            color={selection === "submitted" ? "primary" : "default"}
            variant={selection === "submitted" ? "filled" : "outlined"}
            onClick={() => setSelection("submitted")}
            disabled={disabled}
          />
          <Chip
            label='Latest version'
            size='small'
            color={selection === "latest" ? "primary" : "default"}
            variant={selection === "latest" ? "filled" : "outlined"}
            onClick={() => setSelection("latest")}
            disabled={disabled}
          />
        </Stack>
      )}
      {layout.map((node) => renderLayoutNode(node))}
    </Stack>
  );
};

export default CmFormCaptureFields;
